import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import Modal from "../components/Modal";
import LoadingLogo from "../components/LoadingLogo";
import { refreshAccessToken, isAuthenticated, isSessionAboutToExpire, isAccessTokenExpired, isAutoRefreshWindowExpired } from '../utils/auth';
import { healthService } from '../utils/api';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, fetchUserData, isServerOffline, retryConnection, logout } = useUser();
  const [isSessionWarningVisible, setSessionWarningVisible] = useState(false);
  const [isExtendingSession, setIsExtendingSession] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const warningShownAtRef = useRef<number | null>(null);

  const showSessionWarning = () => {
    if (!isSessionWarningVisible) {
      warningShownAtRef.current = Date.now();
      setSessionWarningVisible(true);
    }
  };

  const hideSessionWarning = () => {
    warningShownAtRef.current = null;
    setSessionWarningVisible(false);
  };

  useEffect(() => {
    // Fetch user data if not already loaded and not in server offline state
    if (!user && !loading && !isServerOffline && isAuthenticated()) {
      console.log("ProtectedRoute: User not loaded, fetching user data");
      fetchUserData();
    }

    // Set up a session expiration check
    const checkSessionExpiration = async () => {
      if (!isAuthenticated()) {
        console.log("ProtectedRoute: User not authenticated, redirecting to login");
        navigate('/login');
        return;
      }

      // FIRST: Check if access token has actually expired (30 minutes) - attempt automatic refresh
      if (isAccessTokenExpired()) {
        console.log("ProtectedRoute: Access token expired, attempting automatic refresh");
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          console.log("ProtectedRoute: Automatic refresh failed, forcing logout");
          hideSessionWarning();
          logout(); // Force logout
          return;
        }
      }

      const sessionExpired = isAutoRefreshWindowExpired();

      // SECOND: Check if session auto-refresh window has expired - SHOW WARNING then LOGOUT after grace
      if (sessionExpired) {
        console.log("ProtectedRoute: Session auto-refresh window expired, showing warning");
        showSessionWarning();

        // If the warning has been visible for more than 2 minutes, log out
        if (
          warningShownAtRef.current &&
          Date.now() - warningShownAtRef.current > 2 * 60 * 1000
        ) {
          console.log("ProtectedRoute: Session window exceeded grace period, logging out");
          hideSessionWarning();
          logout();
        }
        return;
      }

      // THIRD: Check if session is about to hit the 12 hour window - SHOW WARNING
      if (isSessionAboutToExpire() && !isSessionWarningVisible && !isExtendingSession) {
        console.log("ProtectedRoute: Session about to expire (12 hours), showing warning modal");
        showSessionWarning();
      }

      // Dismiss warning modal if session is no longer about to expire (token was refreshed)
      if (!isSessionAboutToExpire() && isSessionWarningVisible && !isExtendingSession) {
        console.log("ProtectedRoute: Session refreshed, dismissing warning modal");
        hideSessionWarning();
      }
    };

    // Run the check every 30 seconds for better responsiveness
    const interval = setInterval(() => {
      checkSessionExpiration();
    }, 30 * 1000);

    // Also check immediately
    checkSessionExpiration();

    return () => clearInterval(interval);
  }, [
    navigate,
    user,
    loading,
    fetchUserData,
    isServerOffline,
    location,
    isSessionWarningVisible,
    isExtendingSession,
    logout,
  ]);

  const handleExtendSession = async () => {
    setIsExtendingSession(true);
    try {
      const success = await refreshAccessToken({ extendSession: true });
      if (!success) {
        // If extension failed, redirect to login
        navigate('/login');
      }
    } catch (error) {
      console.error("Failed to extend session:", error);
      navigate('/login');
    } finally {
      setIsExtendingSession(false);
      hideSessionWarning();
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const handleRetryConnection = async () => {
    setIsRetrying(true);
    try {
      const isOnline = await healthService.checkServerStatus();
      if (isOnline) {
        // If server is online, attempt to fetch user data
        await retryConnection();
      }
    } catch (error) {
      console.error("Failed to check server status:", error);
    } finally {
      setIsRetrying(false);
    }
  };

  // Show server offline modal if server is offline
  if (isServerOffline) {
    return (
      <>
        {children}
        <Modal
          isOpen={true}
          title="Server Offline"
          description="The server is currently unreachable. Please check your connection or try again later."
          confirmText={isRetrying ? "Retrying..." : "Retry"}
          cancelText="Logout"
          onConfirm={handleRetryConnection}
          onCancel={handleLogout}
          disableConfirm={isRetrying}
        />
      </>
    );
  }

  // Show loading state while checking authentication
  if (loading) {
    return <LoadingLogo text="Checking authentication..." />;
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated()) {
    console.log("ProtectedRoute: User not authenticated, redirecting to login");
    return <Navigate to="/login" replace />;
  }

  // If authenticated but no user data, show loading (user data is being fetched)
  if (!user && isAuthenticated()) {
    return <LoadingLogo text="Loading user data..." />;
  }

  // Return children if authenticated and user data is loaded
  return (
    <>
      {children}
      <Modal
        isOpen={isSessionWarningVisible}
        title="Session Expiring"
        description="Your session will expire in less than 2 minutes. If you don't extend it, you'll be automatically logged out when the session expires."
        confirmText={isExtendingSession ? "Extending..." : "Yes, Extend Session"}
        cancelText="Dismiss"
        onConfirm={handleExtendSession}
        onCancel={() => setSessionWarningVisible(false)}
        disableConfirm={isExtendingSession}
      />
    </>
  );
};

export default ProtectedRoute; 
